import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, openAuthedFile } from "@/api/client";
import { formatPrice, formatDate } from "@ckgo2/shared/utils";
import {
  TRIP_DATE_LIFECYCLES,
  TRIP_DATE_LIFECYCLE_LABELS,
  OCCUPANCY_BADGES,
  OCCUPANCY_LABELS,
  ROOM_TYPES,
  ROOM_TYPE_LABELS,
  type OccupancyLevel,
  type DepartureFulfillment,
} from "@ckgo2/shared/types";
import ServiceBookingsEditor from "@/components/common/ServiceBookingsEditor";
import GuideLinkPanel from "@/components/departures/GuideLinkPanel";
import FulfillmentGrid from "@/components/departures/FulfillmentGrid";
import EntryTasksPanel from "@/components/departures/EntryTasksPanel";
import {
  DeparturePhotosPanel,
  type DeparturePhoto,
} from "@/components/departures/DeparturePhotosPanel";
import {
  ArrowLeft,
  Users,
  TrendingUp,
  Download,
  Eye,
  EyeOff,
  Save,
  Loader2,
  Mail,
  Send,
  Wand2,
  ClipboardList,
  AlertCircle,
  Plane,
} from "lucide-react";

// Flight-block deadline chip styling (Block 4.10 / phase 2) — mirrors the season board chip.
const DEADLINE_SHORT: Record<"deposit" | "foc" | "ticketing", string> = {
  deposit: "Deposit",
  foc: "FOC release",
  ticketing: "Ticketing",
};
const FLIGHT_SEV: Record<"ok" | "warn" | "urgent", string> = {
  ok: "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300",
  warn: "bg-amber-100 text-amber-700",
  urgent: "bg-red-100 text-red-700",
};
function flightCountdown(days: number | null): string {
  if (days == null) return "";
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return "today";
  return `${days}d`;
}

interface ManifestRow {
  id: string;
  orderId: string;
  orderNumber?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  nationality?: string | null;
  passportNumber?: string | null;
  passportMasked?: boolean;
  passportExpiry?: string | null;
  dietary?: string | null;
  isLeadPax?: boolean;
  roomType?: string | null;
}

interface OrderRow {
  id: string;
  orderNumber: string;
  status: string;
  adults: number;
  children: number;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}

interface BreakEven {
  fixedCost: number;
  variableCostPerPax: number;
  avgRevenuePerPax: number;
  contributionPerPax: number;
  breakEvenPax: number | null;
}

interface DepartureDetail {
  tripDate: {
    id: string;
    startDate: string;
    endDate: string;
    capacity: number;
    bookedCount: number;
    waitlistCount: number;
    lifecycle: string;
    occupancy: OccupancyLevel;
    code?: string | null;
    leadUserId?: string | null;
    minPax?: number | null;
    guideId?: string | null;
    sharedGuideId?: string | null;
    roomingNotes?: string | null;
    guideMessage?: string | null;
    meetingPoint?: string | null;
    meetingTime?: string | null;
    meetingNote?: string | null;
    infoMeetAt?: string | null;
    infoMeetLink?: string | null;
    infoMeetDoneAt?: string | null;
    notes?: string | null;
    price: number;
  };
  garant?: { id: string; name: string; source: "departure" | "destination" } | null;
  trip?: {
    id: string;
    title: string;
    slug: string;
    defaultMeetingPoint?: string | null;
    defaultMeetingTime?: string | null;
  } | null;
  guide?: { id: string; name: string; email?: string | null } | null;
  sharedGuide?: { id: string; name: string } | null;
  guideAssignments?: Array<{
    id: string;
    guideId: string;
    guideName: string | null;
    role: "lead" | "co" | "backup";
    status: "tentative" | "confirmed";
  }>;
  orders: OrderRow[];
  cancelledOrders: number;
  manifest: ManifestRow[];
  sharedServices: unknown[];
  flightBlocks: Array<{
    id: string;
    airline: string;
    label: string;
    pnr?: string | null;
    currency: string;
    seatsTotal: number;
    used: number;
    remaining: number;
    atRisk: number | null;
    nextDeadline: "deposit" | "foc" | "ticketing" | null;
    dueDate: string | null;
    daysTo: number | null;
    severity: "ok" | "warn" | "urgent";
    actionText: string;
  }>;
  dailyPhotos: DeparturePhoto[];
  margin: {
    revenue: number;
    cost: number;
    margin: number;
    marginPct: number;
    pax: number;
    orders: number;
    currency: string;
    breakEven: BreakEven;
  };
  summary: {
    counts: {
      capacity: number;
      booked: number;
      waitlist: number;
      orders: number;
      cancelledOrders: number;
      pax: number;
    };
    payments: { unpaid: number; depositPaid: number; fullyPaid: number };
    overduePayments: number;
    services: {
      draft: number;
      requested: number;
      confirmed: number;
      paid: number;
      cancelled: number;
    };
    servicesAwaitingConfirmation: number;
    customizationOrders: number;
  };
  fulfillment: DepartureFulfillment;
  reveal: boolean;
}

export default function DepartureDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [reveal, setReveal] = useState(false);
  const [broadcast, setBroadcast] = useState<{ subject: string; body: string } | null>(null);
  const [draftTopic, setDraftTopic] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["departure", id, reveal],
    queryFn: () =>
      api.get<{ data: DepartureDetail }>(`/api/admin/departures/${id}?reveal=${reveal ? 1 : 0}`),
    enabled: !!id,
  });
  const { data: guidesData } = useQuery({
    queryKey: ["guides", "lookup"],
    queryFn: () => api.get<{ data: { id: string; name: string }[] }>("/api/admin/guides?limit=100"),
  });
  const { data: usersData } = useQuery({
    queryKey: ["users", "lookup"],
    queryFn: () => api.get<{ data: { id: string; name: string }[] }>("/api/admin/users?limit=100"),
  });

  const d = data?.data;
  const guides = guidesData?.data ?? [];
  const users = usersData?.data ?? [];

  // Local edit state for the velín header (seeded once data loads).
  const [edit, setEdit] = useState<null | {
    capacity: number;
    waitlistCount: number;
    lifecycle: string;
    code: string;
    leadUserId: string;
    minPax: string;
    roomingNotes: string;
    guideMessage: string;
    meetingPoint: string;
    meetingTime: string;
    meetingNote: string;
    infoMeetAt: string;
    infoMeetLink: string;
  }>(null);
  const editing =
    edit ??
    (d
      ? {
          capacity: d.tripDate.capacity,
          waitlistCount: d.tripDate.waitlistCount,
          lifecycle: d.tripDate.lifecycle,
          code: d.tripDate.code ?? "",
          leadUserId: d.tripDate.leadUserId ?? "",
          minPax: d.tripDate.minPax != null ? String(d.tripDate.minPax) : "",
          roomingNotes: d.tripDate.roomingNotes ?? "",
          guideMessage: d.tripDate.guideMessage ?? "",
          meetingPoint: d.tripDate.meetingPoint ?? "",
          meetingTime: d.tripDate.meetingTime ?? "",
          meetingNote: d.tripDate.meetingNote ?? "",
          infoMeetAt: d.tripDate.infoMeetAt ? d.tripDate.infoMeetAt.slice(0, 16) : "",
          infoMeetLink: d.tripDate.infoMeetLink ?? "",
        }
      : null);

  const save = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.patch(`/api/admin/departures/${id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["departure", id] });
      setEdit(null);
    },
  });

  // The full velín edit payload — shared by the main Save button AND "Mark info meeting as held",
  // so the held stamp carries any in-progress (not-yet-saved) edits instead of discarding them
  // when save.onSuccess clears `edit`.
  const buildVelinPayload = (): Record<string, unknown> =>
    editing
      ? {
          capacity: editing.capacity,
          waitlistCount: editing.waitlistCount,
          lifecycle: editing.lifecycle,
          code: editing.code.trim() || null,
          leadUserId: editing.leadUserId || null,
          minPax: editing.minPax.trim() === "" ? null : Number(editing.minPax),
          roomingNotes: editing.roomingNotes || null,
          guideMessage: editing.guideMessage || null,
          meetingPoint: editing.meetingPoint.trim() || null,
          meetingTime: editing.meetingTime.trim() || null,
          meetingNote: editing.meetingNote.trim() || null,
          infoMeetAt: editing.infoMeetAt || null,
          infoMeetLink: editing.infoMeetLink.trim() || null,
        }
      : {};

  // Guide assignment is set via the schedule route (R-A) — guideId/sharedGuideId on the departure
  // are a sync-only mirror of confirmed guide_assignments, never a direct PATCH. The dropdowns
  // confirm the single lead/co; richer multi-guide planning lives in the schedule timeline.
  const setGuide = useMutation({
    mutationFn: (payload: { role: "lead" | "co"; guideId: string | null }) =>
      api.put(`/api/admin/schedule/departures/${id}/guide`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["departure", id] }),
  });

  // Inline rooming edit from the manifest — roomType is ops-managed (written directly to the row,
  // preserved across order saves). Drives the fulfillment grid's "Room" roll-up.
  const setRoom = useMutation({
    mutationFn: (payload: { participantId: string; roomType: string | null }) =>
      api.patch(`/api/admin/departures/${id}/participants/${payload.participantId}`, {
        roomType: payload.roomType,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["departure", id] }),
  });

  // Soft-hide / restore a guide-shared trip photo (Area 5a moderation).
  const moderatePhoto = useMutation({
    mutationFn: (payload: { photoId: string; restore: boolean }) =>
      api.delete(
        `/api/admin/departures/${id}/photos/${payload.photoId}${payload.restore ? "?restore=1" : ""}`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["departure", id] }),
  });

  const sendBroadcast = useMutation({
    mutationFn: (payload: { subject: string; body: string }) =>
      api.post<{ data: { recipients: number; sent: number } }>(
        `/api/admin/departures/${id}/broadcast`,
        payload,
      ),
    onSuccess: () => setBroadcast(null),
  });

  // AI-draft the broadcast from the departure's facts + a topic (Block 4.14 Řez 4) — fills the composer.
  const draftBroadcast = useMutation({
    mutationFn: (topic: string) =>
      api.post<{ data: { subject: string; body: string } }>(
        `/api/admin/departures/${id}/broadcast-draft`,
        { topic },
      ),
    onSuccess: (res) => setBroadcast({ subject: res.data.subject, body: res.data.body }),
  });

  if (isLoading || !d || !editing) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const be = d.margin.breakEven;
  const cur = d.margin.currency;
  const exportCsv = () =>
    openAuthedFile(
      `/api/admin/departures/${id}/manifest.csv${reveal ? "?reveal=1" : ""}`,
      `manifest-${d.trip?.slug ?? id}.csv`,
    );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/departures" className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700">
          <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {d.trip?.title ?? "Departure"}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            {formatDate(d.tripDate.startDate)} – {formatDate(d.tripDate.endDate)} · {d.margin.pax}{" "}
            pax across {d.margin.orders} orders
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* Pax manifest */}
          <div className="card">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
                <Users className="h-5 w-5" /> Pax manifest
                <span className="text-sm font-normal text-gray-400">({d.manifest.length})</span>
              </h2>
              <div className="flex gap-2">
                <button onClick={() => setReveal((v) => !v)} className="btn-ghost btn-sm">
                  {reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  {reveal ? "Hide passports" : "Reveal passports"}
                </button>
                <button onClick={exportCsv} className="btn-ghost btn-sm">
                  <Download className="h-4 w-4" /> CSV
                </button>
              </div>
            </div>
            {d.manifest.length === 0 ? (
              <p className="text-sm text-gray-400">
                No participants yet — they appear as orders add traveller details.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-gray-200 text-xs uppercase text-gray-500 dark:border-gray-700">
                    <tr>
                      {["Name", "Order", "Nationality", "Passport", "Dietary", "Room"].map((h) => (
                        <th key={h} className="px-2 py-2 text-left font-medium">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {d.manifest.map((p) => (
                      <tr key={p.id}>
                        <td className="px-2 py-2 text-gray-900 dark:text-white">
                          {[p.firstName, p.lastName].filter(Boolean).join(" ") || "—"}
                          {p.isLeadPax && (
                            <span className="bg-primary-100 text-primary-700 ml-1 rounded px-1.5 py-0.5 text-[10px] font-medium">
                              lead
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-2">
                          <Link
                            to={`/orders/${p.orderId}`}
                            className="text-primary-600 dark:text-primary-400 font-mono hover:underline"
                          >
                            {p.orderNumber ?? "—"}
                          </Link>
                        </td>
                        <td className="px-2 py-2 text-gray-600">{p.nationality ?? "—"}</td>
                        <td className="px-2 py-2 font-mono text-gray-600">
                          {p.passportNumber ?? "—"}
                        </td>
                        <td className="px-2 py-2 text-gray-600">{p.dietary ?? "—"}</td>
                        <td className="px-2 py-2">
                          <select
                            className="input py-1 text-xs"
                            value={p.roomType ?? ""}
                            disabled={setRoom.isPending}
                            onChange={(e) =>
                              setRoom.mutate({
                                participantId: p.id,
                                roomType: e.target.value || null,
                              })
                            }
                          >
                            <option value="">—</option>
                            {ROOM_TYPES.map((rt) => (
                              <option key={rt} value={rt}>
                                {ROOM_TYPE_LABELS[rt]}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Fulfillment grid (B4.3) — self-filling cockpit derived from the velín response */}
          <FulfillmentGrid data={d.fulfillment} />

          {/* Entry paperwork (B5.4) — arrival cards & visas filed per traveller */}
          <EntryTasksPanel tripDateId={d.tripDate.id} />

          {/* Orders on this departure */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              Orders <span className="text-sm font-normal text-gray-400">({d.orders.length})</span>
            </h2>
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {d.orders.map((o) => (
                <li key={o.id} className="flex items-center justify-between py-2.5">
                  <div>
                    <Link
                      to={`/orders/${o.id}`}
                      className="text-primary-600 dark:text-primary-400 font-mono text-sm hover:underline"
                    >
                      {o.orderNumber}
                    </Link>
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      {[o.firstName, o.lastName].filter(Boolean).join(" ") || "—"}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500">
                    {o.adults + o.children} pax · {o.status}
                  </span>
                </li>
              ))}
              {d.orders.length === 0 && <li className="py-2 text-sm text-gray-400">No orders.</li>}
            </ul>
          </div>

          {/* Shared services (departure-level) */}
          <ServiceBookingsEditor tripDateId={d.tripDate.id} currency={cur} pax={d.margin.pax} />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Roll-up summary (B4.1.2) — counts · payments · what's left to book */}
          <div className="card">
            <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
              <ClipboardList className="h-5 w-5" /> Summary
            </h2>
            <dl className="space-y-1.5 text-sm">
              <Row
                label="Booked / capacity"
                value={`${d.summary.counts.pax} pax · ${d.summary.counts.booked}/${d.summary.counts.capacity}${
                  d.summary.counts.waitlist > 0 ? ` (+${d.summary.counts.waitlist} wl)` : ""
                }`}
              />
              <Row
                label="Orders"
                value={`${d.summary.counts.orders} live${
                  d.summary.counts.cancelledOrders > 0
                    ? ` · ${d.summary.counts.cancelledOrders} cancelled`
                    : ""
                }`}
              />
              <div className="my-2 border-t border-gray-100 dark:border-gray-700" />
              <Row
                label="Payments"
                value={`${d.summary.payments.fullyPaid} paid · ${d.summary.payments.depositPaid} deposit · ${d.summary.payments.unpaid} unpaid`}
              />
              {d.summary.overduePayments > 0 && (
                <div className="bg-error-50 text-error-700 dark:bg-error-900/20 dark:text-error-400 flex items-center gap-2 rounded-lg p-2 text-xs">
                  <AlertCircle className="h-3.5 w-3.5" />
                  {d.summary.overduePayments} order
                  {d.summary.overduePayments > 1 ? "s" : ""} past a payment deadline
                </div>
              )}
              <div className="my-2 border-t border-gray-100 dark:border-gray-700" />
              <Row
                label="Services"
                value={`${d.summary.services.confirmed + d.summary.services.paid} ready · ${d.summary.servicesAwaitingConfirmation} pending`}
              />
              {d.summary.servicesAwaitingConfirmation > 0 && (
                <div className="bg-warning-50 text-warning-700 dark:bg-warning-900/20 dark:text-warning-400 flex items-center gap-2 rounded-lg p-2 text-xs">
                  <AlertCircle className="h-3.5 w-3.5" />
                  {d.summary.servicesAwaitingConfirmation} service
                  {d.summary.servicesAwaitingConfirmation > 1 ? "s" : ""} still to confirm/book
                </div>
              )}
              {d.summary.customizationOrders > 0 && (
                <Row
                  label="Customizations"
                  value={`${d.summary.customizationOrders} order${d.summary.customizationOrders > 1 ? "s" : ""}`}
                />
              )}
            </dl>
          </div>

          {/* Flight blocks (B4.10 phase 2) — drawdown + the three deadlines + money at risk, where
              the garant looks. Only rendered when this departure holds a group allotment. */}
          {d.flightBlocks.length > 0 && (
            <div className="card">
              <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
                <Plane className="h-5 w-5" /> Flight blocks
              </h2>
              <ul className="space-y-2 text-sm">
                {d.flightBlocks.map((b) => (
                  <li
                    key={b.id}
                    className="rounded-lg border border-gray-100 p-2.5 dark:border-gray-700"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <Link
                        to={`/flights/${b.id}`}
                        className="text-primary-600 dark:text-primary-400 font-medium hover:underline"
                      >
                        {b.airline} · {b.label}
                      </Link>
                      <span className="tabular-nums text-gray-600 dark:text-gray-300">
                        {b.used}/{b.seatsTotal} sold
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      {b.nextDeadline ? (
                        <span
                          className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${FLIGHT_SEV[b.severity]}`}
                        >
                          {DEADLINE_SHORT[b.nextDeadline]} · {flightCountdown(b.daysTo)}
                          {b.dueDate ? ` (${formatDate(b.dueDate)})` : ""}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">no deadline set</span>
                      )}
                      {b.atRisk != null && b.atRisk > 0 && (
                        <span className="text-xs font-medium text-red-600">
                          −{b.atRisk.toLocaleString("en-US")} {b.currency} at risk
                        </span>
                      )}
                    </div>
                    {b.actionText && <p className="mt-1 text-xs text-gray-500">{b.actionText}</p>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Trip photos (Area 5a) — daily photos the guide shared from the road, with office
              moderation (hide / restore). */}
          <DeparturePhotosPanel
            photos={d.dailyPhotos}
            onModerate={(photoId, restore) => moderatePhoto.mutate({ photoId, restore })}
          />

          {/* Break-even */}
          <div className="card">
            <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
              <TrendingUp className="h-5 w-5" /> Break-even
            </h2>
            <dl className="space-y-1.5 text-sm">
              <Row label="Revenue" value={formatPrice(d.margin.revenue, cur)} />
              <Row label="Cost" value={formatPrice(d.margin.cost, cur)} />
              <Row
                label="Margin"
                value={`${formatPrice(d.margin.margin, cur)} (${d.margin.marginPct}%)`}
                strong
                positive={d.margin.margin >= 0}
              />
              <div className="my-2 border-t border-gray-100 dark:border-gray-700" />
              <Row label="Fixed cost / departure" value={formatPrice(be.fixedCost, cur)} />
              <Row label="Avg revenue / pax" value={formatPrice(be.avgRevenuePerPax, cur)} />
              <Row label="Variable cost / pax" value={formatPrice(be.variableCostPerPax, cur)} />
              <Row label="Contribution / pax" value={formatPrice(be.contributionPerPax, cur)} />
              <Row
                label="Break-even pax"
                value={be.breakEvenPax == null ? "—" : `${be.breakEvenPax} (have ${d.margin.pax})`}
                strong
                positive={be.breakEvenPax != null && d.margin.pax >= be.breakEvenPax}
              />
            </dl>
          </div>

          {/* Velín settings */}
          <div className="card">
            <h2 className="mb-3 text-lg font-semibold text-gray-900 dark:text-white">Departure</h2>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <Field label="Capacity">
                  <input
                    type="number"
                    className="input"
                    value={editing.capacity}
                    onChange={(e) => setEdit({ ...editing, capacity: Number(e.target.value) })}
                  />
                </Field>
                <Field label="Waitlist">
                  <input
                    type="number"
                    className="input"
                    value={editing.waitlistCount}
                    onChange={(e) => setEdit({ ...editing, waitlistCount: Number(e.target.value) })}
                  />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Code">
                  <input
                    type="text"
                    className="input font-mono"
                    placeholder="2610AUWA"
                    value={editing.code}
                    onChange={(e) => setEdit({ ...editing, code: e.target.value })}
                  />
                </Field>
                <Field label="Min pax (go/no-go)">
                  <input
                    type="number"
                    className="input"
                    min={0}
                    placeholder="—"
                    value={editing.minPax}
                    onChange={(e) => setEdit({ ...editing, minPax: e.target.value })}
                  />
                </Field>
              </div>
              <Field label="Lifecycle">
                <select
                  className="input"
                  value={editing.lifecycle}
                  onChange={(e) => setEdit({ ...editing, lifecycle: e.target.value })}
                >
                  {TRIP_DATE_LIFECYCLES.map((lc) => (
                    <option key={lc} value={lc}>
                      {TRIP_DATE_LIFECYCLE_LABELS[lc]}
                    </option>
                  ))}
                </select>
              </Field>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Occupancy (derived)</span>
                <span
                  className={`rounded px-2 py-0.5 text-xs font-medium ${OCCUPANCY_BADGES[d.tripDate.occupancy]}`}
                >
                  {OCCUPANCY_LABELS[d.tripDate.occupancy]} · {d.tripDate.bookedCount}/
                  {d.tripDate.capacity}
                </span>
              </div>
              <Field label="Garant (departure owner)">
                <select
                  className="input"
                  value={editing.leadUserId}
                  onChange={(e) => setEdit({ ...editing, leadUserId: e.target.value })}
                >
                  <option value="">
                    {d.garant && d.garant.source === "destination"
                      ? `— inherit: ${d.garant.name} (destination) —`
                      : "— none —"}
                  </option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Lead guide (confirmed)">
                <select
                  className="input"
                  value={d.tripDate.guideId ?? ""}
                  disabled={setGuide.isPending}
                  onChange={(e) =>
                    setGuide.mutate({ role: "lead", guideId: e.target.value || null })
                  }
                >
                  <option value="">— none —</option>
                  {guides.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Co-guide (confirmed)">
                <select
                  className="input"
                  value={d.tripDate.sharedGuideId ?? ""}
                  disabled={setGuide.isPending}
                  onChange={(e) => setGuide.mutate({ role: "co", guideId: e.target.value || null })}
                >
                  <option value="">— none —</option>
                  {guides.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </Field>
              {d.guideAssignments && d.guideAssignments.length > 0 && (
                <p className="text-xs text-gray-500">
                  {d.guideAssignments
                    .map(
                      (a) =>
                        `${a.guideName ?? "?"} (${a.role}${a.status === "tentative" ? ", tentative" : ""})`,
                    )
                    .join(" · ")}
                </p>
              )}
              <Field label="Rooming notes">
                <textarea
                  className="input"
                  rows={3}
                  value={editing.roomingNotes}
                  onChange={(e) => setEdit({ ...editing, roomingNotes: e.target.value })}
                  placeholder="Room 1: Smith ×2 (double); Room 2: Nguyen (single)…"
                />
              </Field>
              <Field label="Guide message (shown in portal once the guide is confirmed)">
                <textarea
                  className="input"
                  rows={3}
                  value={editing.guideMessage}
                  onChange={(e) => setEdit({ ...editing, guideMessage: e.target.value })}
                  placeholder="Hi everyone, I'm your guide for this trip — can't wait to show you around…"
                />
              </Field>
              {/* Structured meeting point / sraz (B4.4) — point/time inherit the trip default
                  (shown as placeholder); override only the exception. Shown in portal + guide PWA. */}
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <Field label="Meeting point">
                    <input
                      className="input"
                      value={editing.meetingPoint}
                      onChange={(e) => setEdit({ ...editing, meetingPoint: e.target.value })}
                      placeholder={
                        d.trip?.defaultMeetingPoint
                          ? `Inherits: ${d.trip.defaultMeetingPoint}`
                          : "e.g. Sheraton Hanoi lobby"
                      }
                    />
                  </Field>
                </div>
                <Field label="Time">
                  <input
                    className="input"
                    value={editing.meetingTime}
                    onChange={(e) => setEdit({ ...editing, meetingTime: e.target.value })}
                    placeholder={
                      d.trip?.defaultMeetingTime
                        ? `Inherits: ${d.trip.defaultMeetingTime}`
                        : "08:00"
                    }
                  />
                </Field>
              </div>
              <Field label="Meeting note (departure-specific instructions)">
                <textarea
                  className="input"
                  rows={2}
                  value={editing.meetingNote}
                  onChange={(e) => setEdit({ ...editing, meetingNote: e.target.value })}
                  placeholder="Look for the guide holding a Banh Mi sign. Bring your passport…"
                />
              </Field>
              {/* Pre-departure info meeting (Block 5.6 Rez 4) - schedule + link + done stamp.
                  Leaving it empty within T-14 raises a task for the garant (deadline scan). */}
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Field label="Info meeting">
                    <input
                      type="datetime-local"
                      className="input"
                      value={editing.infoMeetAt}
                      onChange={(e) => setEdit({ ...editing, infoMeetAt: e.target.value })}
                    />
                  </Field>
                </div>
                <div className="col-span-2">
                  <Field label="Meeting link (Google Meet)">
                    <input
                      className="input"
                      value={editing.infoMeetLink}
                      onChange={(e) => setEdit({ ...editing, infoMeetLink: e.target.value })}
                      placeholder="https://meet.google.com/..."
                    />
                  </Field>
                </div>
              </div>
              {d.tripDate.infoMeetDoneAt ? (
                <p className="text-xs text-green-600">
                  Info meeting held {new Date(d.tripDate.infoMeetDoneAt).toLocaleDateString()}
                </p>
              ) : d.tripDate.infoMeetAt ? (
                <button
                  type="button"
                  onClick={() =>
                    save.mutate({
                      ...buildVelinPayload(),
                      infoMeetDoneAt: new Date().toISOString(),
                    })
                  }
                  className="btn-ghost btn-sm"
                >
                  Mark info meeting as held
                </button>
              ) : null}
              <button
                onClick={() => save.mutate(buildVelinPayload())}
                disabled={save.isPending || !edit}
                className="btn-primary btn-sm w-full"
              >
                {save.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {save.isSuccess && !edit ? "Saved ✓" : "Save departure"}
              </button>
            </div>
          </div>

          {/* Broadcast */}
          <div className="card">
            <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
              <Mail className="h-5 w-5" /> Message travellers
            </h2>
            {!broadcast ? (
              <button
                onClick={() => setBroadcast({ subject: "", body: "" })}
                className="btn-ghost btn-sm w-full"
              >
                <Mail className="h-4 w-4" /> Compose message
              </button>
            ) : (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input
                    className="input flex-1"
                    placeholder="Topic to draft from (e.g. meeting point moved to the hotel lobby at 8am)"
                    value={draftTopic}
                    onChange={(e) => setDraftTopic(e.target.value)}
                  />
                  <button
                    onClick={() => draftBroadcast.mutate(draftTopic)}
                    disabled={draftBroadcast.isPending || !draftTopic.trim()}
                    className="btn-ghost btn-sm whitespace-nowrap"
                    title="Draft subject + body with AI"
                  >
                    {draftBroadcast.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Wand2 className="h-4 w-4" />
                    )}
                    Draft with AI
                  </button>
                </div>
                <input
                  className="input"
                  placeholder="Subject"
                  value={broadcast.subject}
                  onChange={(e) => setBroadcast({ ...broadcast, subject: e.target.value })}
                />
                <textarea
                  className="input"
                  rows={5}
                  placeholder="Your message to every confirmed traveller on this departure…"
                  value={broadcast.body}
                  onChange={(e) => setBroadcast({ ...broadcast, body: e.target.value })}
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => sendBroadcast.mutate(broadcast)}
                    disabled={
                      sendBroadcast.isPending || !broadcast.subject.trim() || !broadcast.body.trim()
                    }
                    className="btn-primary btn-sm"
                  >
                    {sendBroadcast.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                    Send
                  </button>
                  <button onClick={() => setBroadcast(null)} className="btn-ghost btn-sm">
                    Cancel
                  </button>
                </div>
                {sendBroadcast.data && (
                  <p className="text-xs text-emerald-600">
                    Sent to {sendBroadcast.data.data.sent}/{sendBroadcast.data.data.recipients}{" "}
                    travellers.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Guide app link */}
          <GuideLinkPanel tripDateId={d.tripDate.id} />
        </div>
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  strong,
  positive,
}: {
  label: string;
  value: string;
  strong?: boolean;
  positive?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-gray-500">{label}</dt>
      <dd
        className={`${strong ? "font-semibold" : ""} ${
          positive === undefined
            ? "text-gray-900 dark:text-white"
            : positive
              ? "text-emerald-600"
              : "text-red-600"
        }`}
      >
        {value}
      </dd>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="label text-xs">{label}</label>
      {children}
    </div>
  );
}
